import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "INR"];

export default function Layout({ children, year, setYear }) {
  const { user, logout, setCurrency } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItem = ({ isActive }) =>
    `px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
      isActive
        ? "text-[#CFA66A] border-b border-[#CFA66A]"
        : "text-[#A39A8E] hover:text-[#EBE5D9] border-b border-transparent"
    }`;

  return (
    <div className="min-h-screen grain relative">
      <header className="sticky top-0 z-40 bg-[#0F0E0C]/85 backdrop-blur-xl border-b border-[#2E2A25]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center h-16 gap-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-[#CFA66A] flex items-center justify-center">
              <span className="font-mono text-[#CFA66A] text-xs">FJ</span>
            </div>
            <span className="font-head text-base text-[#EBE5D9] hidden sm:inline">
              FOREX JOURNAL
            </span>
          </div>

          <nav className="flex gap-1 flex-1">
            <NavLink to="/today" data-testid={TID.navToday} className={navItem}>
              Today
            </NavLink>
            <NavLink to="/calendar" data-testid={TID.navCalendar} className={navItem}>
              Calendar
            </NavLink>
            <NavLink to="/patterns" data-testid={TID.navPatterns} className={navItem}>
              Patterns
            </NavLink>
            <NavLink to="/community" data-testid="nav-community" className={navItem}>
              Community
            </NavLink>
            <NavLink to="/leaderboard" data-testid="nav-leaderboard" className={navItem}>
              Leaderboard
            </NavLink>
            <NavLink to="/profile" data-testid="nav-profile" className={navItem}>
              Profile
            </NavLink>
            {!user?.is_pro && (
              <NavLink to="/pricing" data-testid="nav-upgrade" className={({ isActive }) =>
                `px-3 py-1 ml-2 self-center font-mono text-xs uppercase tracking-[0.2em] rounded-sm transition-colors ${
                  isActive
                    ? "bg-[#CFA66A] text-[#0F0E0C]"
                    : "border border-[#CFA66A] text-[#CFA66A] hover:bg-[#CFA66A]/10"
                }`
              }>
                Upgrade
              </NavLink>
            )}
            {user?.is_pro && (
              <NavLink to="/pricing" data-testid="nav-pro-badge" className="px-3 py-1 ml-2 self-center font-mono text-xs uppercase tracking-[0.2em] rounded-sm bg-[#CFA66A]/20 text-[#CFA66A] border border-[#CFA66A]">
                Pro
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {year != null && setYear && (
              <select
                data-testid={TID.yearSelect}
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] px-3 py-1.5 text-xs font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            )}
            <select
              data-testid={TID.currencySelect}
              value={user?.currency || "USD"}
              onChange={(e) => setCurrency(e.target.value)}
              className="bg-[#1A1815] border border-[#2E2A25] text-[#EBE5D9] px-3 py-1.5 text-xs font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <div className="hidden md:flex flex-col items-end">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                Trader
              </span>
              <span className="text-sm text-[#EBE5D9]">{user?.name || user?.email}</span>
            </div>
            <button
              data-testid={TID.logoutBtn}
              onClick={handleLogout}
              className="bg-transparent border border-[#2E2A25] text-[#EBE5D9] hover:bg-[#1A1815] hover:border-[#CFA66A] transition-colors px-3 py-1.5 text-xs font-mono rounded-sm"
            >
              LOGOUT
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-8 relative z-10">{children}</main>
    </div>
  );
}
